import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { Alert, Button } from '@mui/material';

import { IMaterialPrice, IMaterialPriceLight } from '../../types/material';

import { useUserContext } from '../../contexts/userContext';

import { UtilityService } from '../../services/utilityService';
import { MaterialPriceService } from '../../services/materialPriceService';

import { Icons } from '../icons';
import Json from "../json";

import '../../styles/vendorMaterialPricingTable.scss';
import { IInternalResponse } from '../../types/base';

export interface IVendorMaterialPricingTableProps {
	id: string;
	data: IMaterialPriceLight[];
	customer?: string;
	material?: string;
	date?: Date;
	hideControls?: boolean;
	validate?: boolean;
	unloadData: () => void;
}

const VendorMaterialPricingTable: React.FunctionComponent<IVendorMaterialPricingTableProps> = ({ id, data, customer, material, date, hideControls = false, validate = false, unloadData }) => {
	const userContext = useUserContext();
	const navigate = useNavigate();
	const [isSaving, setIsSaving] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string>();
	const [sortField, setSortField] = useState("customer");
	const [sortAscending, setSortAscending] = useState(true);

	const [pricingValidationErrors, setPricingValidationErrors] = useState<IInternalResponse[]>([]);

	const [dataView, setDataView] = useState<IMaterialPriceLight[]>([]);
	const [firstVisibleRow, setFirstVisibleRow] = useState(0);
	const [lastVisibleRow, setLastVisibleRow] = useState(9);
	const [pageSize, setPageSize] = useState(10);

	useEffect(() => {
		if (data) {
			let newData = data.filter((item) => {
				if (customer && customer != "All" && item.customerName != customer) {
					return false;
				}
				if (material && material != "All" && item.materialName != material) {
					return false;
				}

				return true;
			}).slice();

			if (sortField) {
				switch (sortField) {
					case "material": {
						newData.sort((a, b) => UtilityService.compareStrings(a.materialName, b.materialName));
						break;
					}
					case "category": {
						newData.sort((a, b) => UtilityService.compareStrings(a.customerCategoryName, b.customerCategoryName));
						break;
					}
					case "price": {
						newData.sort((a, b) => UtilityService.compareNumbers(a.price, b.price));
						break;
					}
					case "start": {
						newData.sort((a, b) => UtilityService.compareStrings(a.startDate?.toString(), b.startDate?.toString()));
						break;
					}
					case "end": {
						newData.sort((a, b) => UtilityService.compareStrings(a.endDate?.toString(), b.endDate?.toString()));
						break;
					}
					default: {
						newData.sort((a, b) => UtilityService.compareStrings(a.customerName, b.customerName));
						break;
					}
				}
			}
			if (!sortAscending) newData.reverse();

			if (validate) {
				var validationResponse = MaterialPriceService.validateMaterialPricesForUpload(data);
				setPricingValidationErrors(validationResponse);
			}

			setDataView(newData);
			setFirstVisibleRow(0);
			setLastVisibleRow(pageSize - 1);
		}
	}, [data, customer, material, date, sortAscending])

	const handleApproveClick = () => {
		setIsSaving(true);
		setErrorMessage(undefined);

			MaterialPriceService.uploadMaterialPricing(data)
				.then((response) => {
					console.log('MaterialService.uploadMaterialPricing: SUCCESS', response);
					navigate("/vendor/" + id);
				})
				.catch((error) => {
					console.log('MaterialService.uploadMaterialPricing: ERROR', error);
					setErrorMessage("There was an issue uploading your pricing data. Please try again.");
				})
				.finally(() => {
					setIsSaving(false);
				});
	}

	const handleHeaderClick = (field: string) => {
		if (sortField == field) {
			setSortAscending(!sortAscending);
		}
		else {
			setSortAscending(true);
			setSortField(field);
		}
	}

	const handlePageBack = () => {
		setFirstVisibleRow(firstVisibleRow - pageSize);
		setLastVisibleRow(lastVisibleRow - pageSize);
	}

	const handlePageForward = () => {
		setFirstVisibleRow(firstVisibleRow + pageSize);
		setLastVisibleRow(lastVisibleRow + pageSize);
	}

	return (
		<div className="vendor-material-table">
			{!hideControls &&
				<div className="controls">
					{pricingValidationErrors.length == 0 &&
						<Button color="secondary" variant='contained' onClick={handleApproveClick} disabled={isSaving}>{isSaving ? "Saving..." : "Save"}</Button>
					}
					<Button color="primary" variant='contained' onClick={unloadData}>Go Back</Button>
				</div>
			}
					{pricingValidationErrors.map((item, index) => (
						<Alert className="alert-error" severity="warning" key={index} >
							{item.message}
						</Alert>
					))}
					<div className="headers">
						<div className="col-customer">
							<p onClick={() => handleHeaderClick("customer")}>
								Customer
								{" "}
								{(sortField == "customer" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
								{(sortField == "customer" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
							</p>
						</div>

						<div className="col-material">
							<p onClick={() => handleHeaderClick("material")}>
								Material
								{" "}
								{(sortField == "material" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
								{(sortField == "material" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
							</p>
						</div>
						{/* <div className="col-category">
							<p onClick={() => handleHeaderClick("category")}>
								Code
								{" "}
								{(sortField == "category" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
								{(sortField == "category" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
							</p>
						</div> */}
						<div className="col-price">
							<p onClick={() => handleHeaderClick("price")}>
								Price
								{" "}
								{(sortField == "price" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
								{(sortField == "price" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
							</p>
						</div>
						<div className="col-start">
							<p onClick={() => handleHeaderClick("start")}>
								Effective
								{" "}
								{(sortField == "start" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
								{(sortField == "start" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
							</p>
						</div>
						<div className="col-end">
							<p onClick={() => handleHeaderClick("end")}>
								End
								{" "}
								{(sortField == "end" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
								{(sortField == "end" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
							</p>
						</div>
					</div>
					<div className="data">
						{data && data.length > 0 ?
							(
								dataView.map((item, index) => (
									<div className={index >= firstVisibleRow && index <= lastVisibleRow ? "row" : "row row-hidden"} key={item.id}>
										<div className="col-customer"><p>{item.customerName}</p></div>
										<div className="col-material"><p>{item.materialName}</p></div>
										{/* <div className="col-category"><p>{item.customerCategoryName}</p></div> */}
										<div className="col-price"><p>{item.price}</p></div>
										<div className="col-start"><p>{item.startDate ? moment(item.startDate).format("MM/DD/YYYY") : ""}</p></div>
										<div className="col-start"><p>{item.endDate ? moment(item.endDate).format("MM/DD/YYYY") : ""}</p></div>
									</div>
								))
							) : (<div className="no-results">0 results</div>)}
					</div>
					<div className="pager">
						<div className="pager-button"><Button variant="outlined" fullWidth={true} disabled={firstVisibleRow == 0 ? true : false} onClick={handlePageBack}>Previous</Button></div>
						<div className="pager-text">{`${firstVisibleRow + 1} to ${(lastVisibleRow + 1) <= dataView.length ? lastVisibleRow + 1 : dataView.length} of ${dataView.length}`}</div>
						<div className="pager-button"><Button variant="outlined" fullWidth={true} disabled={lastVisibleRow >= dataView.length - 1 ? true : false} onClick={handlePageForward}>Next</Button></div>
					</div>
			{/* <Json data={date} title="Date Filter:"></Json> */}
		</div>
	)
}

export default VendorMaterialPricingTable;